import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { BaseButton } from '../../../shared/ui';
import { type VerifyCodeState } from '../model/use-verify-form';

type VerifyResendTimerProps = {
  codeState: VerifyCodeState;
  codeError?: string;
  isResendAvailable: boolean;
  onResendCode: () => void;
  resendCountdown: string;
};

const ResendTimerRow = styled.div.attrs({
  'data-ui': 'auth-verify-resend-timer',
})`
  align-self: center;
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.scale.px(5)};
  color: ${({ theme }) => theme.colors.text.subdued};
`;

const ResendLabel = styled.span`
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 500;
  line-height: 1.25;
`;

const ResendCountdown = styled.span`
  font-size: ${({ theme }) => theme.scale.px(14)};
  font-weight: 400;
  line-height: 1.25;
`;

const InvalidCodeMessage = styled.p`
  margin: 0;
  align-self: center;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 500;
  line-height: 1;
  color: ${({ theme }) => theme.colors.support.danger};
`;

const ResendAction = styled(BaseButton)`
  align-self: center;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 500;
  line-height: 1.25;
  vertical-align: bottom;
  color: ${({ theme }) => theme.colors.text.subdued};
  text-decoration: underline;
`;

export function VerifyResendTimer({
  codeState,
  codeError,
  isResendAvailable,
  onResendCode,
  resendCountdown,
}: VerifyResendTimerProps) {
  const { t } = useTranslation();

  if (isResendAvailable) {
    return (
      <ResendAction type="button" onClick={onResendCode}>
        {t('verify.resendAction')}
      </ResendAction>
    );
  }

  if (codeState === 'error') {
    return (
      <InvalidCodeMessage>
        {codeError ?? t('verify.invalidCode')}
      </InvalidCodeMessage>
    );
  }

  return (
    <ResendTimerRow>
      <ResendLabel>{t('verify.resendLabel')}</ResendLabel>
      <ResendCountdown>{resendCountdown}</ResendCountdown>
    </ResendTimerRow>
  );
}
